import { Injectable, OnDestroy, inject } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import CallKit, {
  VideoCallPictureInPictureDeactivateOptions,
  VideoCallPictureInPictureOptions,
  VideoCallPictureInPictureState,
} from '@plugins/android_callkit/callkit.plugin';
import { AppStateChangeService } from '@services/app-state-change.service';
import { PlatformService } from '@services/platform.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { buildAvatarInitials, buildAvatarSvgDataUrl } from '@utils/initials';

interface NativeVideoCallPictureInPictureFallback {
  readonly fullName: string;
  readonly avatarUrl?: string | null;
}

interface NativeVideoCallPictureInPictureBaseOptions {
  readonly orderId: number;
  readonly aspectRatioWidth: number;
  readonly aspectRatioHeight: number;
  readonly fallback: NativeVideoCallPictureInPictureFallback;
  readonly preferSpeakerAudio?: boolean;
}

interface NativeVideoCallPictureInPictureFrameMetricsElement extends HTMLVideoElement {
  webkitDecodedFrameCount?: number;
  mozPaintedFrames?: number;
}

interface NativeVideoCallPictureInPictureTrackListeners {
  readonly mute: () => void;
  readonly unmute: () => void;
  readonly ended: () => void;
}

interface NativeVideoCallPictureInPictureStreamListeners {
  readonly addtrack: EventListener;
  readonly removetrack: EventListener;
}

@Injectable({
  providedIn: 'root'
})
export class NativeVideoCallPictureInPictureService implements OnDestroy {
  private readonly appStateChangeService: AppStateChangeService = inject(AppStateChangeService);
  private readonly platformService: PlatformService = inject(PlatformService);

  private readonly isActiveSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isActive$: Observable<boolean> = this.isActiveSubject.asObservable();

  private readonly sb: SubscriptionsBag = new SubscriptionsBag();
  private readonly browserVisibilityStateChangeHandler: () => void = (): void => {
    if (!this.currentOrderId) return;
    this.queuePictureInPictureSync(this.getSyncDelayMsForCurrentBrowserState());
  };

  private currentOrderId: number | null = null;
  private lastManagedOrderId: number | null = null;
  private currentAspectRatioWidth: number = 9;
  private currentAspectRatioHeight: number = 16;
  private currentPreferSpeakerAudio: boolean = false;
  private trackedVideoElement: HTMLVideoElement | null = null;
  private fallback: NativeVideoCallPictureInPictureFallback | null = null;
  private snapshotSource: 'video' | 'fallback' | null = null;
  private detachedVideoElement: HTMLVideoElement | null = null;
  private detachedVideoHostElement: HTMLDivElement | null = null;
  private snapshotCanvas: HTMLCanvasElement | null = null;
  private snapshotTimeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
  private videoFrameStallTimeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
  private videoFramePollTimeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
  private videoFallbackRecoveryTimeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
  private queuedPictureInPictureSyncTimeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
  private trackedVideoStream: MediaStream | null = null;
  private trackedVideoStreamListeners: NativeVideoCallPictureInPictureStreamListeners | null = null;
  private readonly trackedVideoTrackListeners: Map<MediaStreamTrack, NativeVideoCallPictureInPictureTrackListeners> =
    new Map<MediaStreamTrack, NativeVideoCallPictureInPictureTrackListeners>();
  private pictureInPictureListener: PluginListenerHandle | null = null;
  private nativePictureInPicturePrepared: boolean = false;
  private videoFrameMonitorVersion: number = 0;
  private videoFramesStalled: boolean = false;
  private readonly videoFrameStallMs: number = 2500;
  private readonly videoFramePollIntervalMs: number = 500;
  private readonly videoFallbackRecoveryIntervalMs: number = 1000;
  private readonly snapshotLoopIntervalMs: number = 200;

  constructor() {
    this.subscribeToAppStateChanges();
    this.subscribeToBrowserVisibilityStateChanges();
    void this.registerPictureInPictureListener();
  }

  public ngOnDestroy(): void {
    this.sb.unsubscribeAll();
    this.clearQueuedPictureInPictureSync();
    this.stopSnapshotLoop();
    this.stopVideoFrameFreshnessMonitor();
    this.unsubscribeFromBrowserVisibilityStateChanges();
    void this.unregisterPictureInPictureListener();
    void this.deactivate({finalizeAudioSession: true});
  }

  public get isActive(): boolean {
    return this.isActiveSubject.value;
  }

  public async activateForElement(options: NativeVideoCallPictureInPictureBaseOptions & {
    readonly sourceVideoElement: HTMLVideoElement;
  }): Promise<void> {
    if (!this.platformService.isIosApp) return;

    this.setCurrentPictureInPictureOptions(options);
    await this.setTrackedVideoElement(options.sourceVideoElement);
    this.startVideoFrameFreshnessMonitor(options.sourceVideoElement);
    this.snapshotSource = this.videoFramesStalled ? 'fallback' : 'video';
    this.syncVideoFallbackRecoveryCheck();
    await this.syncPictureInPicture();
  }

  public async activateForStream(options: NativeVideoCallPictureInPictureBaseOptions & {
    readonly sourceStream: MediaStream;
  }): Promise<void> {
    if (!this.platformService.isIosApp) return;

    const previousStream: MediaStream | null = this.getTrackedVideoStream();
    const videoElement: HTMLVideoElement | null = await this.ensureDetachedVideoElement(options.sourceStream);
    if (!videoElement) return;

    this.setCurrentPictureInPictureOptions(options);
    await this.setTrackedVideoElement(videoElement);
    this.startVideoFrameFreshnessMonitor(videoElement);
    this.snapshotSource = this.shouldUseFallbackUntilFreshFrame(previousStream, options.sourceStream)
      || this.videoFramesStalled
      ? 'fallback'
      : 'video';
    this.syncVideoFallbackRecoveryCheck();
    await this.syncPictureInPicture();
  }

  public async activateForFallback(options: NativeVideoCallPictureInPictureBaseOptions): Promise<void> {
    if (!this.platformService.isIosApp) return;

    this.setCurrentPictureInPictureOptions(options);
    this.stopVideoFrameFreshnessMonitor();
    this.trackedVideoElement = null;
    this.snapshotSource = 'fallback';
    this.detachDetachedVideoElement();
    await this.syncPictureInPicture();
  }

  public async deactivate(options: VideoCallPictureInPictureDeactivateOptions = {}): Promise<void> {
    if (!this.platformService.isIosApp) return;

    this.clearQueuedPictureInPictureSync();
    this.stopSnapshotLoop();
    this.stopVideoFrameFreshnessMonitor();
    this.currentOrderId = null;
    this.currentAspectRatioWidth = 9;
    this.currentAspectRatioHeight = 16;
    this.currentPreferSpeakerAudio = false;
    this.trackedVideoElement = null;
    this.fallback = null;
    this.snapshotSource = null;
    this.detachDetachedVideoElement();
    this.snapshotCanvas = null;
    this.nativePictureInPicturePrepared = false;
    this.isActiveSubject.next(false);

    try {
      await CallKit.deactivateVideoCallPictureInPicture(options);
      if (options.finalizeAudioSession === true) {
        this.lastManagedOrderId = null;
      }
    } catch (error) {
      console.error('Failed to deactivate native iOS video call picture-in-picture', error);
    }
  }

  public async deactivateForOrder(
    orderId: number | null,
    options: VideoCallPictureInPictureDeactivateOptions = {},
  ): Promise<void> {
    if (!this.platformService.isIosApp) return;
    if (orderId === null) return;
    if (this.currentOrderId !== orderId && this.lastManagedOrderId !== orderId) return;

    await this.deactivate(options);
  }

  private subscribeToAppStateChanges(): void {
    if (!this.platformService.isIosApp) return;

    this.sb.sub = this.appStateChangeService.appStateChange$.subscribe((isActive: boolean): void => {
      if (!this.currentOrderId) return;
      this.queuePictureInPictureSync(isActive ? this.getSyncDelayMsForCurrentBrowserState() : 0);
    });
  }

  private subscribeToBrowserVisibilityStateChanges(): void {
    if (!this.platformService.isIosApp || this.platformService.isSSR) return;
    if (typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;

    document.addEventListener('visibilitychange', this.browserVisibilityStateChangeHandler);
    window.addEventListener('focus', this.browserVisibilityStateChangeHandler);
    window.addEventListener('pageshow', this.browserVisibilityStateChangeHandler);
  }

  private unsubscribeFromBrowserVisibilityStateChanges(): void {
    if (this.platformService.isSSR) return;
    if (typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;

    document.removeEventListener('visibilitychange', this.browserVisibilityStateChangeHandler);
    window.removeEventListener('focus', this.browserVisibilityStateChangeHandler);
    window.removeEventListener('pageshow', this.browserVisibilityStateChangeHandler);
  }

  private async registerPictureInPictureListener(): Promise<void> {
    if (!this.platformService.isIosApp) return;
    if (this.pictureInPictureListener) return;

    this.pictureInPictureListener = await CallKit.addListener(
      'pictureInPictureChanged',
      (state: VideoCallPictureInPictureState): void => {
        this.isActiveSubject.next(state.active);
        if (!state.active && this.appStateChangeService.appIsActive) {
          this.stopSnapshotLoop();
        }
      },
    );
  }

  private async unregisterPictureInPictureListener(): Promise<void> {
    if (!this.pictureInPictureListener) return;
    await this.pictureInPictureListener.remove();
    this.pictureInPictureListener = null;
  }

  private async setTrackedVideoElement(videoElement: HTMLVideoElement): Promise<void> {
    if (this.trackedVideoElement === videoElement) {
      await this.tryPlay(videoElement);
      return;
    }

    this.trackedVideoElement = videoElement;
    this.videoFramesStalled = false;
    await this.tryPlay(videoElement);
  }

  private setCurrentPictureInPictureOptions(options: NativeVideoCallPictureInPictureBaseOptions): void {
    this.currentOrderId = options.orderId;
    this.lastManagedOrderId = options.orderId;
    this.currentAspectRatioWidth = options.aspectRatioWidth;
    this.currentAspectRatioHeight = options.aspectRatioHeight;
    this.currentPreferSpeakerAudio = options.preferSpeakerAudio === true;
    this.fallback = options.fallback;
  }

  private async ensureDetachedVideoElement(stream: MediaStream): Promise<HTMLVideoElement | null> {
    if (this.platformService.isSSR) return null;
    if (typeof document === 'undefined') return null;

    this.ensureDetachedVideoHostElement();

    if (!this.detachedVideoElement) {
      const videoElement: HTMLVideoElement = document.createElement('video');
      videoElement.autoplay = true;
      videoElement.muted = true;
      videoElement.playsInline = true;
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('webkit-playsinline', '');
      videoElement.style.position = 'fixed';
      videoElement.style.right = '0';
      videoElement.style.bottom = '0';
      videoElement.style.width = '2px';
      videoElement.style.height = '2px';
      videoElement.style.opacity = '0.01';
      this.detachedVideoElement = videoElement;
      this.detachedVideoHostElement?.appendChild(videoElement);
    }

    if (this.detachedVideoElement.srcObject !== stream) {
      this.detachedVideoElement.srcObject = stream;
      this.videoFramesStalled = false;
    }

    await this.tryPlay(this.detachedVideoElement);
    return this.detachedVideoElement;
  }

  private ensureDetachedVideoHostElement(): void {
    if (this.platformService.isSSR) return;
    if (typeof document === 'undefined') return;
    if (this.detachedVideoHostElement) return;

    const hostElement: HTMLDivElement = document.createElement('div');
    hostElement.style.position = 'fixed';
    hostElement.style.right = '0';
    hostElement.style.bottom = '0';
    hostElement.style.width = '2px';
    hostElement.style.height = '2px';
    hostElement.style.opacity = '0.01';
    hostElement.style.pointerEvents = 'none';
    hostElement.style.overflow = 'hidden';
    hostElement.style.zIndex = '-1';
    document.body.appendChild(hostElement);
    this.detachedVideoHostElement = hostElement;
  }

  private detachDetachedVideoElement(): void {
    if (this.detachedVideoElement) {
      this.detachedVideoElement.pause();
      this.detachedVideoElement.srcObject = null;
      this.detachedVideoElement.remove();
      this.detachedVideoElement = null;
    }

    if (this.detachedVideoHostElement) {
      this.detachedVideoHostElement.remove();
      this.detachedVideoHostElement = null;
    }
  }

  private async syncPictureInPicture(): Promise<void> {
    if (!this.platformService.isIosApp) return;
    if (!this.currentOrderId || !this.hasSnapshotSource()) return;

    const isPrepared: boolean = await this.ensureNativePictureInPicturePrepared();
    if (!isPrepared) {
      this.stopSnapshotLoop();
      return;
    }

    await this.pushSnapshotIfPossible();

    if (this.shouldKeepPictureInPictureActive()) {
      this.startSnapshotLoop();
      return;
    }

    this.stopSnapshotLoop();
  }

  private shouldKeepPictureInPictureActive(): boolean {
    if (!this.currentOrderId || !this.hasSnapshotSource()) return false;
    return !this.appStateChangeService.appIsActive || this.isBrowserDocumentHidden();
  }

  private async ensureNativePictureInPicturePrepared(): Promise<boolean> {
    if (!this.currentOrderId || !this.hasSnapshotSource()) return false;

    if (this.trackedVideoElement) {
      await this.tryPlay(this.trackedVideoElement);
    }
    if (this.nativePictureInPicturePrepared) {
      return true;
    }

    const options: VideoCallPictureInPictureOptions = {
      orderId: this.currentOrderId,
      hasRemoteVideo: true,
      aspectRatioWidth: this.currentAspectRatioWidth,
      aspectRatioHeight: this.currentAspectRatioHeight,
      preferSpeakerAudio: this.currentPreferSpeakerAudio,
    };

    try {
      await CallKit.activateVideoCallPictureInPicture(options);
      this.nativePictureInPicturePrepared = true;
      return true;
    } catch (error) {
      console.error('Failed to activate native iOS video call picture-in-picture', error);
      this.nativePictureInPicturePrepared = false;
      this.isActiveSubject.next(false);
      return false;
    }
  }

  private startSnapshotLoop(): void {
    if (this.snapshotSource !== 'video') return;
    if (!this.currentOrderId || !this.trackedVideoElement) return;
    if (this.snapshotTimeoutId) return;

    const tick = async (): Promise<void> => {
      this.snapshotTimeoutId = null;

      if (!this.currentOrderId || !this.trackedVideoElement || this.snapshotSource !== 'video') {
        return;
      }

      await this.pushSnapshotIfPossible();
      this.snapshotTimeoutId = globalThis.setTimeout((): void => {
        void tick();
      }, this.snapshotLoopIntervalMs);
    };

    this.snapshotTimeoutId = globalThis.setTimeout((): void => {
      void tick();
    }, 0);
  }

  private stopSnapshotLoop(): void {
    const timeoutId: ReturnType<typeof globalThis.setTimeout> | null = this.snapshotTimeoutId;
    this.snapshotTimeoutId = null;

    if (!timeoutId) return;

    globalThis.clearTimeout(timeoutId);
  }

  private startVideoFrameFreshnessMonitor(videoElement: HTMLVideoElement): void {
    this.videoFrameMonitorVersion += 1;
    this.clearVideoFrameStallTimeout();
    this.clearVideoFramePollTimeout();
    this.clearVideoFallbackRecoveryTimeout();
    this.syncTrackedVideoTrackListeners(videoElement);
    this.videoFramesStalled = this.hasExplicitlyInactiveTrackedVideo();

    const monitorVersion: number = this.videoFrameMonitorVersion;
    this.scheduleVideoFrameStallTimeout(monitorVersion);

    if (typeof videoElement.requestVideoFrameCallback !== 'function') {
      this.startVideoFramePoll(videoElement, monitorVersion);
      return;
    }

    let lastPresentedFrames: number | null = null;
    let lastMediaTime: number | null = null;
    const onFrame: VideoFrameRequestCallback = (
      _now: DOMHighResTimeStamp,
      metadata: VideoFrameCallbackMetadata,
    ): void => {
      if (monitorVersion !== this.videoFrameMonitorVersion) return;

      const isFreshFrame: boolean = this.isFreshVideoFrame(metadata, lastPresentedFrames, lastMediaTime);
      lastPresentedFrames = metadata.presentedFrames;
      lastMediaTime = metadata.mediaTime;

      if (isFreshFrame) {
        this.handleFreshVideoFrame(monitorVersion);
      }

      videoElement.requestVideoFrameCallback(onFrame);
    };

    videoElement.requestVideoFrameCallback(onFrame);
  }

  private startVideoFramePoll(videoElement: HTMLVideoElement, monitorVersion: number): void {
    let lastFrameCount: number | null = this.getVideoFrameCount(videoElement);

    const poll = (): void => {
      if (monitorVersion !== this.videoFrameMonitorVersion) return;

      const currentFrameCount: number | null = this.getVideoFrameCount(videoElement);
      if (currentFrameCount !== null && lastFrameCount !== null && currentFrameCount > lastFrameCount) {
        lastFrameCount = currentFrameCount;
        this.handleFreshVideoFrame(monitorVersion);
      } else if (currentFrameCount !== null && lastFrameCount === null) {
        lastFrameCount = currentFrameCount;
      }

      this.scheduleVideoFramePoll(monitorVersion, poll);
    };

    this.scheduleVideoFramePoll(monitorVersion, poll);
  }

  private handleFreshVideoFrame(monitorVersion: number): void {
    if (monitorVersion !== this.videoFrameMonitorVersion) return;

    this.videoFramesStalled = false;
    this.clearVideoFallbackRecoveryTimeout();
    this.scheduleVideoFrameStallTimeout(monitorVersion);

    if (this.snapshotSource === 'video') return;

    this.snapshotSource = 'video';
    void this.syncPictureInPicture();
  }

  private scheduleVideoFrameStallTimeout(monitorVersion: number): void {
    this.clearVideoFrameStallTimeout();

    this.videoFrameStallTimeoutId = globalThis.setTimeout((): void => {
      this.videoFrameStallTimeoutId = null;
      this.handleVideoFrameStalled(monitorVersion);
    }, this.videoFrameStallMs);
  }

  private handleVideoFrameStalled(monitorVersion: number): void {
    if (monitorVersion !== this.videoFrameMonitorVersion) return;
    if (!this.trackedVideoElement || !this.fallback) return;

    if (!this.shouldSwitchToFallbackAfterVideoFrameStall()) {
      this.scheduleVideoFrameStallTimeout(monitorVersion);
      return;
    }

    this.videoFramesStalled = true;
    if (this.snapshotSource === 'fallback') {
      this.syncVideoFallbackRecoveryCheck();
      return;
    }

    this.snapshotSource = 'fallback';
    this.stopSnapshotLoop();
    this.syncVideoFallbackRecoveryCheck();
    void this.pushSnapshotIfPossible();
  }

  private scheduleVideoFramePoll(monitorVersion: number, poll: () => void): void {
    this.clearVideoFramePollTimeout();
    this.videoFramePollTimeoutId = globalThis.setTimeout((): void => {
      if (monitorVersion !== this.videoFrameMonitorVersion) return;
      poll();
    }, this.videoFramePollIntervalMs);
  }

  private stopVideoFrameFreshnessMonitor(): void {
    this.videoFrameMonitorVersion += 1;
    this.videoFramesStalled = false;
    this.clearVideoFrameStallTimeout();
    this.clearVideoFramePollTimeout();
    this.clearVideoFallbackRecoveryTimeout();
    this.clearTrackedVideoStreamListeners();
    this.clearTrackedVideoTrackListeners();
  }

  private clearVideoFrameStallTimeout(): void {
    const timeoutId: ReturnType<typeof globalThis.setTimeout> | null = this.videoFrameStallTimeoutId;
    this.videoFrameStallTimeoutId = null;
    if (!timeoutId) return;

    globalThis.clearTimeout(timeoutId);
  }

  private syncVideoFallbackRecoveryCheck(): void {
    this.clearVideoFallbackRecoveryTimeout();
    if (this.snapshotSource !== 'fallback' || !this.trackedVideoElement) return;

    this.videoFallbackRecoveryTimeoutId = globalThis.setTimeout((): void => {
      this.videoFallbackRecoveryTimeoutId = null;
      this.handleTrackedVideoTrackStateChanged();
      this.syncVideoFallbackRecoveryCheck();
    }, this.videoFallbackRecoveryIntervalMs);
  }

  private clearVideoFallbackRecoveryTimeout(): void {
    const timeoutId: ReturnType<typeof globalThis.setTimeout> | null = this.videoFallbackRecoveryTimeoutId;
    this.videoFallbackRecoveryTimeoutId = null;
    if (!timeoutId) return;

    globalThis.clearTimeout(timeoutId);
  }

  private clearVideoFramePollTimeout(): void {
    const timeoutId: ReturnType<typeof globalThis.setTimeout> | null = this.videoFramePollTimeoutId;
    this.videoFramePollTimeoutId = null;
    if (!timeoutId) return;

    globalThis.clearTimeout(timeoutId);
  }

  private isFreshVideoFrame(
    metadata: VideoFrameCallbackMetadata,
    lastPresentedFrames: number | null,
    lastMediaTime: number | null,
  ): boolean {
    if (lastPresentedFrames === null || lastMediaTime === null) return true;
    return metadata.presentedFrames !== lastPresentedFrames || metadata.mediaTime !== lastMediaTime;
  }

  private getVideoFrameCount(videoElement: NativeVideoCallPictureInPictureFrameMetricsElement): number | null {
    if (typeof videoElement.getVideoPlaybackQuality === 'function') {
      const quality: VideoPlaybackQuality = videoElement.getVideoPlaybackQuality();
      if (Number.isFinite(quality.totalVideoFrames)) return quality.totalVideoFrames;
    }

    if (typeof videoElement.webkitDecodedFrameCount === 'number' && Number.isFinite(videoElement.webkitDecodedFrameCount)) {
      return videoElement.webkitDecodedFrameCount;
    }

    if (typeof videoElement.mozPaintedFrames === 'number' && Number.isFinite(videoElement.mozPaintedFrames)) {
      return videoElement.mozPaintedFrames;
    }

    return null;
  }

  private syncTrackedVideoTrackListeners(videoElement: HTMLVideoElement): void {
    this.clearTrackedVideoStreamListeners();
    this.clearTrackedVideoTrackListeners();

    const stream: MediaStream | null = this.getVideoElementStream(videoElement);
    if (!stream) return;

    const streamListeners: NativeVideoCallPictureInPictureStreamListeners = {
      addtrack: (): void => this.handleTrackedVideoStreamChanged(),
      removetrack: (): void => this.handleTrackedVideoStreamChanged(),
    };

    stream.addEventListener('addtrack', streamListeners.addtrack);
    stream.addEventListener('removetrack', streamListeners.removetrack);
    this.trackedVideoStream = stream;
    this.trackedVideoStreamListeners = streamListeners;

    for (const track of stream.getVideoTracks()) {
      const listeners: NativeVideoCallPictureInPictureTrackListeners = {
        mute: (): void => this.handleTrackedVideoTrackStateChanged(),
        unmute: (): void => this.handleTrackedVideoTrackStateChanged(),
        ended: (): void => this.handleTrackedVideoTrackStateChanged(),
      };

      track.addEventListener('mute', listeners.mute);
      track.addEventListener('unmute', listeners.unmute);
      track.addEventListener('ended', listeners.ended);
      this.trackedVideoTrackListeners.set(track, listeners);
    }
  }

  private clearTrackedVideoTrackListeners(): void {
    this.trackedVideoTrackListeners.forEach((
      listeners: NativeVideoCallPictureInPictureTrackListeners,
      track: MediaStreamTrack,
    ): void => {
      track.removeEventListener('mute', listeners.mute);
      track.removeEventListener('unmute', listeners.unmute);
      track.removeEventListener('ended', listeners.ended);
    });
    this.trackedVideoTrackListeners.clear();
  }

  private clearTrackedVideoStreamListeners(): void {
    const stream: MediaStream | null = this.trackedVideoStream;
    const listeners: NativeVideoCallPictureInPictureStreamListeners | null = this.trackedVideoStreamListeners;
    this.trackedVideoStream = null;
    this.trackedVideoStreamListeners = null;
    if (!stream || !listeners) return;

    stream.removeEventListener('addtrack', listeners.addtrack);
    stream.removeEventListener('removetrack', listeners.removetrack);
  }

  private handleTrackedVideoStreamChanged(): void {
    const videoElement: HTMLVideoElement | null = this.trackedVideoElement;
    if (!videoElement) return;

    this.syncTrackedVideoTrackListeners(videoElement);
    this.handleTrackedVideoTrackStateChanged();
  }

  private handleTrackedVideoTrackStateChanged(): void {
    if (!this.trackedVideoElement || !this.fallback) return;

    if (this.hasExplicitlyInactiveTrackedVideo()) {
      this.videoFramesStalled = true;
      if (this.snapshotSource === 'fallback') {
        this.syncVideoFallbackRecoveryCheck();
        return;
      }

      this.snapshotSource = 'fallback';
      this.stopSnapshotLoop();
      this.syncVideoFallbackRecoveryCheck();
      void this.pushSnapshotIfPossible();
      return;
    }

    this.videoFramesStalled = false;
    this.clearVideoFallbackRecoveryTimeout();
    this.scheduleVideoFrameStallTimeout(this.videoFrameMonitorVersion);
    if (this.snapshotSource === 'video') return;

    this.snapshotSource = 'video';
    void this.syncPictureInPicture();
  }

  private shouldSwitchToFallbackAfterVideoFrameStall(): boolean {
    if (this.hasExplicitlyInactiveTrackedVideo()) return true;
    return !this.isCurrentBrowserBackgrounded();
  }

  private shouldUseFallbackUntilFreshFrame(
    previousStream: MediaStream | null,
    nextStream: MediaStream,
  ): boolean {
    if (!this.isCurrentBrowserBackgrounded()) return false;
    if (previousStream === nextStream) return false;
    return !!this.fallback;
  }

  private hasExplicitlyInactiveTrackedVideo(): boolean {
    const stream: MediaStream | null = this.getTrackedVideoStream();
    if (!stream) return false;

    const videoTracks: MediaStreamTrack[] = stream.getVideoTracks();
    if (videoTracks.length === 0) return true;

    return !videoTracks.some((track: MediaStreamTrack): boolean => {
      if (track.readyState !== 'live') return false;
      return track.muted !== true;
    });
  }

  private getTrackedVideoStream(): MediaStream | null {
    const videoElement: HTMLVideoElement | null = this.trackedVideoElement;
    if (!videoElement) return null;
    return this.getVideoElementStream(videoElement);
  }

  private getVideoElementStream(videoElement: HTMLVideoElement): MediaStream | null {
    if (typeof MediaStream === 'undefined') return null;

    const source: MediaProvider | null = videoElement.srcObject;
    if (source instanceof MediaStream) return source;

    return null;
  }

  private isCurrentBrowserBackgrounded(): boolean {
    return !this.appStateChangeService.appIsActive || this.isBrowserDocumentHidden();
  }

  private async pushSnapshotIfPossible(): Promise<boolean> {
    if (!this.currentOrderId || !this.hasSnapshotSource()) return false;

    const dataUrl: string | null = await this.captureCurrentSnapshot();
    if (!dataUrl) return false;

    try {
      await CallKit.updateVideoCallPictureInPictureSnapshot({
        orderId: this.currentOrderId,
        dataUrl,
      });
      return true;
    } catch (error) {
      console.error('Failed to update native iOS video call picture-in-picture snapshot', error);
      return false;
    }
  }

  private hasSnapshotSource(): boolean {
    if (this.snapshotSource === 'video') {
      return !!this.trackedVideoElement;
    }
    if (this.snapshotSource === 'fallback') {
      return !!this.fallback;
    }
    return false;
  }

  private async captureCurrentSnapshot(): Promise<string | null> {
    if (this.snapshotSource === 'video' && this.trackedVideoElement) {
      return this.captureSnapshot(this.trackedVideoElement);
    }

    if (this.snapshotSource === 'fallback' && this.fallback) {
      return await this.captureFallbackSnapshot(this.fallback);
    }

    return null;
  }

  private captureSnapshot(videoElement: HTMLVideoElement): string | null {
    const videoWidth: number = videoElement.videoWidth;
    const videoHeight: number = videoElement.videoHeight;
    if (videoWidth <= 0 || videoHeight <= 0) return null;

    const canvas: HTMLCanvasElement = this.getSnapshotCanvas();
    const maxWidth: number = 360;
    const targetWidth: number = Math.min(videoWidth, maxWidth);
    const targetHeight: number = Math.max(Math.round(videoHeight * (targetWidth / videoWidth)), 1);

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context: CanvasRenderingContext2D | null = canvas.getContext('2d');
    if (!context) return null;

    context.drawImage(videoElement, 0, 0, targetWidth, targetHeight);

    try {
      return canvas.toDataURL('image/jpeg', 0.72);
    } catch (error) {
      console.error('Failed to capture native iOS video call PiP snapshot', error);
      return null;
    }
  }

  private async captureFallbackSnapshot(fallback: NativeVideoCallPictureInPictureFallback): Promise<string | null> {
    const fallbackAvatarUrl: string | null = this.normalizeImageUrl(fallback.avatarUrl);
    if (fallbackAvatarUrl) {
      const avatarSnapshot: string | null = await this.captureFallbackSnapshotFromSource(
        fallback,
        fallbackAvatarUrl,
        true,
      );
      if (avatarSnapshot) return avatarSnapshot;
    }

    return await this.captureFallbackSnapshotFromSource(
      fallback,
      buildAvatarSvgDataUrl(fallback.fullName),
      false,
    );
  }

  private async captureFallbackSnapshotFromSource(
    fallback: NativeVideoCallPictureInPictureFallback,
    imageSource: string,
    useAnonymousCrossOrigin: boolean,
  ): Promise<string | null> {
    const canvas: HTMLCanvasElement = this.getSnapshotCanvas();
    const targetHeight: number = 640;
    const targetWidth: number = Math.max(
      Math.round(targetHeight * (this.currentAspectRatioWidth / this.currentAspectRatioHeight)),
      1,
    );

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context: CanvasRenderingContext2D | null = canvas.getContext('2d');
    if (!context) return null;

    this.drawFallbackBackground(context, targetWidth, targetHeight);

    const image: HTMLImageElement | null = await this.loadImage(imageSource, useAnonymousCrossOrigin);
    if (image) {
      this.drawFallbackAvatarImage(context, image, targetWidth, targetHeight);
    } else {
      this.drawFallbackInitialsAvatar(context, fallback.fullName, targetWidth, targetHeight);
    }

    return this.readSnapshotCanvas(canvas);
  }

  private drawFallbackBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
  ): void {
    const gradient: CanvasGradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#15181d');
    gradient.addColorStop(1, '#22262d');
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
  }

  private drawFallbackAvatarImage(
    context: CanvasRenderingContext2D,
    image: HTMLImageElement,
    width: number,
    height: number,
  ): void {
    const size: number = Math.round(Math.min(width, height) * 0.56);
    const x: number = Math.round((width - size) / 2);
    const y: number = Math.round((height - size) / 2);
    const sourceWidth: number = image.naturalWidth > 0 ? image.naturalWidth : image.width;
    const sourceHeight: number = image.naturalHeight > 0 ? image.naturalHeight : image.height;
    const sourceSize: number = Math.min(sourceWidth, sourceHeight);

    if (sourceSize <= 0) return;

    const sourceX: number = Math.round((sourceWidth - sourceSize) / 2);
    const sourceY: number = Math.round((sourceHeight - sourceSize) / 2);

    context.save();
    context.beginPath();
    context.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
    context.clip();
    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceSize,
      sourceSize,
      x,
      y,
      size,
      size,
    );
    context.restore();
  }

  private drawFallbackInitialsAvatar(
    context: CanvasRenderingContext2D,
    fullName: string,
    width: number,
    height: number,
  ): void {
    const size: number = Math.round(Math.min(width, height) * 0.56);
    const x: number = Math.round((width - size) / 2);
    const y: number = Math.round((height - size) / 2);
    const initials: string = buildAvatarInitials(fullName);

    context.fillStyle = '#ffffff';
    context.beginPath();
    context.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
    context.fill();

    if (!initials) return;

    context.fillStyle = '#000000';
    context.font = `600 ${Math.round(size * 0.36)}px Roboto, sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(initials, x + size / 2, y + size / 2);
  }

  private loadImage(source: string, useAnonymousCrossOrigin: boolean): Promise<HTMLImageElement | null> {
    return new Promise<HTMLImageElement | null>((resolve): void => {
      if (!source) {
        resolve(null);
        return;
      }

      const image: HTMLImageElement = new Image();
      if (useAnonymousCrossOrigin) {
        image.crossOrigin = 'anonymous';
      }
      image.onload = (): void => resolve(image);
      image.onerror = (): void => resolve(null);
      image.src = source;
    });
  }

  private normalizeImageUrl(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const trimmed: string = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private readSnapshotCanvas(canvas: HTMLCanvasElement): string | null {
    try {
      return canvas.toDataURL('image/jpeg', 0.72);
    } catch (error) {
      console.error('Failed to capture native iOS video call PiP fallback snapshot', error);
      return null;
    }
  }

  private getSnapshotCanvas(): HTMLCanvasElement {
    if (this.snapshotCanvas) {
      return this.snapshotCanvas;
    }

    const canvas: HTMLCanvasElement = document.createElement('canvas');
    this.snapshotCanvas = canvas;
    return canvas;
  }

  private queuePictureInPictureSync(delayMs: number): void {
    if (!this.currentOrderId) return;

    this.clearQueuedPictureInPictureSync();

    if (delayMs <= 0) {
      void this.syncPictureInPicture();
      return;
    }

    this.queuedPictureInPictureSyncTimeoutId = globalThis.setTimeout((): void => {
      this.queuedPictureInPictureSyncTimeoutId = null;
      void this.syncPictureInPicture();
    }, delayMs);
  }

  private clearQueuedPictureInPictureSync(): void {
    const timeoutId: ReturnType<typeof globalThis.setTimeout> | null = this.queuedPictureInPictureSyncTimeoutId;
    this.queuedPictureInPictureSyncTimeoutId = null;

    if (!timeoutId) return;

    globalThis.clearTimeout(timeoutId);
  }

  private getSyncDelayMsForCurrentBrowserState(): number {
    return this.appStateChangeService.appIsActive && !this.isBrowserDocumentHidden() ? 250 : 0;
  }

  private isBrowserDocumentHidden(): boolean {
    if (this.platformService.isSSR) return false;
    if (typeof document === 'undefined') return false;
    return document.hidden;
  }

  private async tryPlay(videoElement: HTMLVideoElement): Promise<void> {
    try {
      await videoElement.play();
    } catch (error) {
      console.warn('Failed to prepare iOS PiP video element', error);
    }
  }
}
